/**
 * Centralized Tab Styling System
 * Provides consistent tab styling across all pages
 * Usage: Import and apply to MUI Tabs components
 */

import brandGradients from '../assets/theme/base/gradients';

/**
 * Main tab styles using gold theme to match unified design system
 * Use this for most pages with tabs
 */
export const tabStyles = {
  // Container styles (applied to <Tabs> component)
  container: {
    '& .MuiTabs-flexContainer': {
      flexWrap: { xs: 'wrap', md: 'nowrap' }, // Responsive wrapping
    },
    '& .MuiTabs-indicator': {
      backgroundColor: '#f4c542', // Gold indicator
      height: 3,
    },
    '& .MuiTab-root': {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.875rem',
      color: '#7b809a', // Gray for inactive
      opacity: 0.8,
      minHeight: 48,
      transition: 'all 0.2s ease-in-out',
      
      '&:hover': {
        color: '#f39c12', // Lighter gold on hover
        opacity: 1,
      },
      
      '&.Mui-selected': {
        color: '#0a1628', // Navy for active tab
        opacity: 1,
        fontWeight: 700,
      },
    },
  },

  /**
   * Variant: Filled tabs with gold background for active state
   * More prominent, good for primary navigation tabs
   */
  filled: {
    '& .MuiTabs-flexContainer': {
      flexWrap: { xs: 'wrap', md: 'nowrap' },
    },
    '& .MuiTabs-indicator': {
      display: 'none', // Hide indicator, use background instead
    },
    '& .MuiTab-root': {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.875rem',
      color: '#7b809a',
      opacity: 0.8,
      minHeight: 48,
      borderRadius: '8px',
      margin: '0 4px',
      transition: 'all 0.2s ease-in-out',
      
      '&:hover': {
        backgroundColor: 'rgba(244, 197, 66, 0.1)',
        color: '#f39c12',
        opacity: 1,
      },
      
      '&.Mui-selected': {
        color: '#ffffff !important',
        background: brandGradients.goldGradient,
        opacity: 1,
        boxShadow: '0 2px 8px rgba(244, 197, 66, 0.3)',
      },
    },
  },

  /**
   * Variant: Navy filled tabs
   * Alternative style with navy background for active state
   */
  navyFilled: {
    '& .MuiTabs-flexContainer': {
      flexWrap: { xs: 'wrap', md: 'nowrap' },
    },
    '& .MuiTabs-indicator': {
      display: 'none',
    },
    '& .MuiTab-root': {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.875rem',
      color: '#7b809a',
      opacity: 0.8,
      minHeight: 48,
      borderRadius: '8px',
      margin: '0 4px',
      transition: 'all 0.2s ease-in-out',
      
      '&:hover': {
        backgroundColor: 'rgba(10, 22, 40, 0.05)',
        color: '#0a1628',
        opacity: 1,
      },
      
      '&.Mui-selected': {
        color: '#f4c542 !important',
        background: brandGradients.navyGlassDark,
        opacity: 1,
        boxShadow: '0 2px 8px rgba(10, 22, 40, 0.2)',
      },
    },
  },

  /**
   * Variant: Minimal tabs
   * Clean, understated style for secondary navigation
   */
  minimal: {
    '& .MuiTabs-indicator': {
      backgroundColor: '#f4c542',
      height: 2,
    },
    '& .MuiTab-root': {
      textTransform: 'none',
      fontWeight: 500,
      fontSize: '0.875rem',
      color: '#7b809a',
      minHeight: 40,
      
      '&:hover': {
        color: '#f39c12',
      },
      
      '&.Mui-selected': {
        color: '#0a1628',
        fontWeight: 600,
      },
    },
  },
};

/**
 * Legacy color mappings for backward compatibility
 * These can be gradually replaced with the new unified styles
 */
export const legacyTabColors = {
  brown: 'rgba(104, 61, 0, 1)',      // Old NotisTunggakan color
  magenta: 'rgb(179, 0, 87)',         // Old HartaDetails color
  gold: '#f4c542',                     // New unified color
};

export default tabStyles;
